// ===========================================
// Calendar Reschedule Confirmation Dialog
// ===========================================

import { useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import EventIcon from '@mui/icons-material/Event';
import RepeatIcon from '@mui/icons-material/Repeat';
import HistoryIcon from '@mui/icons-material/History';
import { RescheduleDialogState } from './calendar.types';
import { parseUTCDate, type LessonWeekWindow } from '../../../utils/dateUtils';
import type { RelatedLesson } from '../../../api/lessons.api';

interface CalendarRescheduleDialogProps {
  dialog: RescheduleDialogState;
  notifyParents: boolean;
  rescheduleReason: string;
  applyToAllTerms: boolean;
  overrideConflicts: boolean;
  relatedLessons?: RelatedLesson[];
  isPending: boolean;
  rescheduleMode: 'single' | 'all';
  fromWeekNumber?: number;
  /**
   * The weeks the class runs (mid-term start / last week). "Apply from week" offers only these.
   * Omitted (window not loaded yet) = the whole term, exactly as before.
   */
  weekWindow?: LessonWeekWindow;
  /**
   * Whether `weekWindow` has settled. The window comes from a separate lesson fetch, so
   * while it loads (or after it fails) every term week would be offered - and a week
   * picked then could vanish from the list when the window arrived. Anything but 'ready'
   * disables "Apply from week" (it falls back to "All weeks"). Default 'ready' = the
   * caller has no window to wait for.
   */
  weekWindowStatus?: 'loading' | 'ready' | 'error';
  onRescheduleModeChange: (mode: 'single' | 'all') => void;
  onNotifyParentsChange: (value: boolean) => void;
  onRescheduleReasonChange: (value: string) => void;
  onApplyToAllTermsChange: (value: boolean) => void;
  onOverrideConflictsChange: (value: boolean) => void;
  onFromWeekNumberChange: (week: number | undefined) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function CalendarRescheduleDialog({
  dialog,
  notifyParents,
  rescheduleReason,
  applyToAllTerms,
  overrideConflicts,
  relatedLessons,
  isPending,
  rescheduleMode,
  fromWeekNumber,
  weekWindow,
  weekWindowStatus = 'ready',
  onRescheduleModeChange,
  onNotifyParentsChange,
  onRescheduleReasonChange,
  onApplyToAllTermsChange,
  onOverrideConflictsChange,
  onFromWeekNumberChange,
  onConfirm,
  onCancel,
}: CalendarRescheduleDialogProps) {
  // Is the occurrence being dragged already in the past? `originalDateKey` is the
  // true original (the drop handler resolves it from an existing exception when
  // there is one), so this is the date the server will judge for its past-aware
  // notify default — keep the two in agreement.
  //
  // Both sides are `YYYY-MM-DD` keys, so this is a plain lexicographic compare with
  // no timezone frame at all. `todayKey` is built from UTC getters to match the
  // server's `startOfTodayUTC` boundary; using LOCAL getters here would read a day
  // ahead east of UTC (Sydney is +10/+11), making today's occurrence look past and
  // silently suppressing the parent email.
  const draggedOccurrenceIsPast = (() => {
    if (!dialog.originalDateKey) return false;
    const now = new Date();
    const todayKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(
      now.getUTCDate()
    ).padStart(2, '0')}`;
    return dialog.originalDateKey < todayKey;
  })();

  // `originalDateKey` is a bare calendar-day key. Parse it to UTC midnight and format in
  // UTC so the weekday and day never shift — date-fns `format` (local) would move both
  // west of UTC.
  const draggedOccurrenceLabel = dialog.originalDateKey
    ? new Intl.DateTimeFormat('en-AU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }).format(parseUTCDate(dialog.originalDateKey))
    : '';

  // "Apply from week" options: the weeks the CLASS runs, not every week of the term.
  const applyFromWeeks = Array.from({ length: dialog.totalWeeks ?? 0 }, (_, i) => i + 1).filter(
    (week) => !weekWindow || (week >= weekWindow.first && week <= weekWindow.last)
  );

  // A chosen from-week that is no longer offered (window arrived and excludes it, or the
  // window is not settled) goes back to the default rather than sitting in a select that
  // has no such option.
  const applyFromWeeksKey = applyFromWeeks.join(',');
  useEffect(() => {
    if (fromWeekNumber === undefined) return;
    if (weekWindowStatus !== 'ready' || !applyFromWeeks.includes(fromWeekNumber)) {
      onFromWeekNumberChange(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromWeekNumber, weekWindowStatus, applyFromWeeksKey]);

  return (
    <Dialog open={dialog.open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Reschedule Lesson
          {dialog.conflicts?.hasConflicts && <WarningIcon color="warning" />}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {dialog.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* Mode toggle: Just this class vs All classes */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                What would you like to move?
              </Typography>
              <ToggleButtonGroup
                value={rescheduleMode}
                exclusive
                onChange={(_e, val) => val && onRescheduleModeChange(val)}
                size="small"
                fullWidth
                aria-label="Reschedule scope"
              >
                <ToggleButton value="single">
                  <EventIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Just this class
                </ToggleButton>
                <ToggleButton value="all">
                  <RepeatIcon sx={{ mr: 0.5 }} fontSize="small" />
                  All classes
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider />

            {/* Event info */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {dialog.event?.title}
              </Typography>
              {rescheduleMode === 'single' ? (
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">
                    <strong>From:</strong>{' '}
                    {dialog.event?.start && format(dialog.event.start, 'EEEE, d MMM')} at{' '}
                    {dialog.event?.start && format(dialog.event.start, 'h:mm a')} -{' '}
                    {dialog.event?.end && format(dialog.event.end, 'h:mm a')}
                  </Typography>
                  <Typography color="text.secondary">
                    <strong>To:</strong>{' '}
                    {dialog.newStart && format(dialog.newStart, 'EEEE, d MMM')} at{' '}
                    {dialog.newStart && format(dialog.newStart, 'h:mm a')} -{' '}
                    {dialog.newEnd && format(dialog.newEnd, 'h:mm a')}
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">
                    <strong>From:</strong>{' '}
                    {dialog.event?.start && format(dialog.event.start, 'EEEE')}s at{' '}
                    {dialog.event?.start && format(dialog.event.start, 'h:mm a')} -{' '}
                    {dialog.event?.end && format(dialog.event.end, 'h:mm a')}
                  </Typography>
                  <Typography color="text.secondary">
                    <strong>To:</strong>{' '}
                    {dialog.newStart && format(dialog.newStart, 'EEEE')}s at{' '}
                    {dialog.newStart && format(dialog.newStart, 'h:mm a')} -{' '}
                    {dialog.newEnd && format(dialog.newEnd, 'h:mm a')}
                  </Typography>
                </Stack>
              )}
              {rescheduleMode === 'single' && (
                <Typography variant="caption" color="text.secondary">
                  Only this occurrence will be moved. All other weeks stay unchanged.
                </Typography>
              )}
              {rescheduleMode === 'all' && (
                <Typography variant="caption" color="text.secondary">
                  All weekly occurrences will permanently move to the new day/time.
                </Typography>
              )}
            </Box>

            {/* Past-occurrence notice. Dragging a past lesson on the calendar
                silently suppresses the parent email (the server's past-aware
                notify default) — without this the admin gets no clue why, which
                is worse than no feature. Mirrors RescheduleClassDialog's warning. */}
            {rescheduleMode === 'single' && draggedOccurrenceIsPast && (
              <Alert severity="warning" icon={<HistoryIcon />}>
                <Typography variant="subtitle2" gutterBottom>
                  This class has already passed
                </Typography>
                <Typography variant="body2">
                  You’re rescheduling {draggedOccurrenceLabel}, which is in the past.
                  This records the class at its new date — useful when a class didn’t
                  run and wasn’t rescheduled at the time.
                  {notifyParents
                    ? ' Parents will be notified.'
                    : ' Parents will not be notified.'}
                </Typography>
              </Alert>
            )}

            {/* Error from conflict check failure */}
            {dialog.error && (
              <Alert severity="error">{dialog.error}</Alert>
            )}

            {/* Conflicts */}
            {dialog.conflicts?.hasConflicts && (
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>
                  Conflicts Detected
                </Typography>
                {dialog.conflicts.teacherConflict && (
                  <Typography variant="body2">
                    Teacher conflict: {dialog.conflicts.teacherConflict.lessonName} on{' '}
                    {format(parseISO(dialog.conflicts.teacherConflict.date), 'EEE d MMM')} at{' '}
                    {dialog.conflicts.teacherConflict.time}
                  </Typography>
                )}
                {dialog.conflicts.roomConflict && (
                  <Typography variant="body2">
                    Room conflict: {dialog.conflicts.roomConflict.lessonName} on{' '}
                    {format(parseISO(dialog.conflicts.roomConflict.date), 'EEE d MMM')} at{' '}
                    {dialog.conflicts.roomConflict.time}
                  </Typography>
                )}
                {dialog.conflicts.scheduleConflict && (
                  <Typography variant="body2">
                    Teacher unavailable: {dialog.conflicts.scheduleConflict.reason}
                  </Typography>
                )}
                <FormControlLabel
                  sx={{ mt: 1, mb: 0, ml: 1 }}
                  control={
                    <Switch
                      checked={overrideConflicts}
                      onChange={(e) => onOverrideConflictsChange(e.target.checked)}
                      color="warning"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="medium">
                      Override conflicts and reschedule anyway
                    </Typography>
                  }
                />
              </Alert>
            )}

            {/* Affected students */}
            {dialog.conflicts && dialog.conflicts.affectedStudents > 0 && (
              <Alert severity="info">
                This will affect {dialog.conflicts.affectedStudents} enrolled{' '}
                {dialog.conflicts.affectedStudents === 1 ? 'student' : 'students'}.
              </Alert>
            )}

            <Divider />

            {/* Week selection - only show for "All classes" mode with multiple weeks */}
            {rescheduleMode === 'all' && dialog.totalWeeks && applyFromWeeks.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Apply from week</InputLabel>
                <Select
                  value={
                    weekWindowStatus === 'ready' && fromWeekNumber !== undefined && applyFromWeeks.includes(fromWeekNumber)
                      ? fromWeekNumber
                      : ''
                  }
                  onChange={(e) => onFromWeekNumberChange(e.target.value ? Number(e.target.value) : undefined)}
                  label="Apply from week"
                  disabled={weekWindowStatus !== 'ready'}
                >
                  <MenuItem value="">
                    <em>All weeks</em>
                  </MenuItem>
                  {applyFromWeeks.map((week) => (
                    <MenuItem key={week} value={week}>
                      Week {week} onwards
                    </MenuItem>
                  ))}
                </Select>
                {weekWindowStatus === 'loading' && (
                  <FormHelperText data-testid="apply-from-week-helper">
                    Loading the weeks this class runs...
                  </FormHelperText>
                )}
                {weekWindowStatus === 'error' && (
                  <FormHelperText error data-testid="apply-from-week-helper">
                    Couldn't load the weeks this class runs, so the change applies to all weeks.
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {/* Apply to all terms option - only show for "All classes" mode with related lessons */}
            {rescheduleMode === 'all' && relatedLessons && relatedLessons.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={applyToAllTerms}
                      onChange={(e) => onApplyToAllTermsChange(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Apply to all terms ({relatedLessons.length + 1} lessons)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Also reschedule: {relatedLessons.map((l) => l.termName).join(', ')}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </Alert>
            )}

            {/* Options */}
            <FormControlLabel
              control={
                <Switch
                  checked={notifyParents}
                  onChange={(e) => onNotifyParentsChange(e.target.checked)}
                />
              }
              label="Notify parents via email"
            />
            {rescheduleMode === 'single' && draggedOccurrenceIsPast && !notifyParents && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Notifications are off by default for a class that has already passed.
              </Typography>
            )}

            <TextField
              label="Reason for rescheduling (optional)"
              multiline
              rows={2}
              value={rescheduleReason}
              onChange={(e) => onRescheduleReasonChange(e.target.value)}
              placeholder="e.g., Teacher unavailable, Room maintenance"
              fullWidth
              inputProps={{ maxLength: 500 }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={dialog.loading || (dialog.conflicts?.hasConflicts && !overrideConflicts) || !dialog.conflicts || isPending}
        >
          {isPending ? 'Rescheduling...' : 'Confirm Reschedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
